import { useEffect, useMemo, useState } from "react";

import { getExtensionValue } from "@medplum/core";
import { Questionnaire } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Extensions } from "@/fhir/enums/extensions";
import { showSystemErrorNotification } from "@/utils/notifications";

import { PhysicalExamQuestionTypeRecord, PhysicalExamSectionRecord } from "../types/physical-exams";

export default function usePhysicalExamQuestionnaire(url: string): {
  sections: Record<string, PhysicalExamSectionRecord | PhysicalExamQuestionTypeRecord>;
  questionnaire: Questionnaire;
} {
  const [questionnaire, setQuestionnaire] = useState<Questionnaire>();
  const medplum = useMedplum();
  useEffect(() => {
    (async () => {
      const questionnaire = await medplum.searchOne("Questionnaire", {
        url,
        status: "active",
        _sort: "-_lastUpdated",
      });
      setQuestionnaire(questionnaire);

      if (!questionnaire) {
        showSystemErrorNotification();
        throw new Error(`Questionnaire not found: ${url}`);
      }
    })();
  }, [medplum, url]);

  const sections: Record<string, PhysicalExamSectionRecord | PhysicalExamQuestionTypeRecord> =
    useMemo(() => {
      if (!questionnaire) return {};

      const groups = questionnaire.item?.filter((item) => item.type === "group");
      if (!groups) {
        showSystemErrorNotification();
        throw new Error(`No groups found in questionnaire: ${url}`);
      }

      return groups.reduce((result, group) => {
        const singleChoiceType = getExtensionValue(group, Extensions.PhysicalExamQuestionType) as
          | string
          | undefined;

        if (singleChoiceType === "single") {
          return {
            ...result,
            [group.linkId]: {
              label: group.text,
              singleValueSet: group.item?.find(
                (item) => item.linkId === `${group.linkId}SingleChoiceDetails`,
              )?.answerValueSet,
            } as PhysicalExamQuestionTypeRecord,
          };
        } else {
          return {
            ...result,
            [group.linkId]: {
              label: group.text,
              normalValueSet: group.item?.find(
                (item) => item.linkId === `${group.linkId}NormalDetails`,
              )?.answerValueSet,
              notNormalValueSet: group.item?.find(
                (item) => item.linkId === `${group.linkId}NotNormalDetails`,
              )?.answerValueSet,
            } as PhysicalExamSectionRecord,
          };
        }
      }, {});
    }, [url, questionnaire]);

  return { sections, questionnaire: questionnaire! };
}
